import { Box, HStack, Link, Stack } from "@chakra-ui/react";

import { STRIPE_WIRE_PRINT_STORAGE_PREFIX } from "@equidefi/constants/payments";
import type { DTO } from "@equidefi/shared/types/dto";
import { Text } from "@equidefi/ui";
import { Icon } from "@equidefi/ui/icon";

import { CurrencyFormatter } from "../../helpers/format";

const InstructionRow: React.FC<{
  label: string;
  value?: string | number | null;
  highlight?: boolean;
}> = ({ label, value, highlight = false }) => {
  if (value == null) return null;
  return (
    <HStack justify="space-between" align="start" spacing={4}>
      <Text textStyle="body2" color="gray.600" m="0">
        {label}
      </Text>
      <Text fontWeight={highlight ? "bold" : "medium"} m="0" textAlign="right">
        {value}
      </Text>
    </HStack>
  );
};

interface StripeWireInstructionsCardProps {
  instructions?: DTO.StripeWireInstructions | null;
  enablePrint?: boolean;
  investmentId?: string;
}

export const StripeWireInstructionsCard: React.FC<StripeWireInstructionsCardProps> = ({
  instructions,
  enablePrint = false,
  investmentId,
}) => {
  if (!instructions) return null;

  const amount =
    instructions.amount_remaining != null ? CurrencyFormatter.format(instructions.amount_remaining / 100) : null;

  const printHref =
    enablePrint && investmentId ? `/vault/investment/${investmentId}/payments/print?type=stripe_wire` : undefined;

  const handlePrintClick = () => {
    if (!investmentId) return;
    try {
      localStorage.setItem(`${STRIPE_WIRE_PRINT_STORAGE_PREFIX}${investmentId}`, JSON.stringify(instructions));
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Box borderWidth="1px" borderRadius="md" p={4}>
      <Stack spacing={2}>
        <InstructionRow label="Amount to wire" value={amount} />
        <InstructionRow label="Bank" value={instructions.bank_name} />
        <InstructionRow label="Beneficiary" value={instructions.account_holder_name} />
        <InstructionRow label="Account number" value={instructions.account_number} />
        <InstructionRow label="Routing number" value={instructions.routing_number} />
        <InstructionRow label="Account type" value={instructions.account_type} />
        <InstructionRow label="Reference (include in memo)" value={instructions.reference} highlight />
      </Stack>
      {(printHref || instructions.hosted_instructions_url) && (
        <HStack spacing={4} mt={4}>
          {printHref && (
            <Link
              href={printHref}
              target="_blank"
              rel="noreferrer"
              onClick={handlePrintClick}
              color="blue.600"
              display="inline-flex"
              alignItems="center"
              gap={2}
              fontWeight="medium">
              <Icon.Printer size="1em" />
              Print Payment Instructions
            </Link>
          )}
          {instructions.hosted_instructions_url && (
            <Link
              href={instructions.hosted_instructions_url}
              target="_blank"
              rel="noreferrer"
              color="blue.600"
              display="inline-flex"
              alignItems="center"
              gap={2}>
              <Icon.ExternalLink size="1em" />
              View on Stripe
            </Link>
          )}
        </HStack>
      )}
    </Box>
  );
};
